import { BusinessInfoForm, ILinksOptions } from "./BusinessInfo.interface";
import { Activation, Merchant, User } from "src/models";
import { action, computed, observable } from "mobx";
import { createContext, useContext } from "react";
import { ENTITY_TYPES } from "src/constants/entityTypes";
import { IActivationJSON, IMerchantJSON, IOutletFiles } from "src/interfaces";
import { cleanObject } from "src/helpers/cleanObject";
import { POSTAL_CODE } from "src/constants/postal_code";
import { AsyncStore, PassportStore } from "src/stores";
import { DEV_PARTNERS } from "src/constants/devPartners";
import { asyncRequest } from "src/decorators";
import { fromEnv } from "../../../helpers";

export type IPassportParsedPostalComboBox = {
  postal_code?: string;
  city?: string;
  province?: string;
};

export class BusinessInfoState extends AsyncStore<BusinessInfoState> {
  constructor(public passportStore?: PassportStore) {
    super();
  }

  @observable
  hasPhysicalBusiness = false;

  @observable
  websiteOption = "";

  @observable
  linksOption: ILinksOptions = "";

  @observable
  shipping = "";

  @observable
  selectedPostalComboBoxOptions = "";

  @observable
  inputErrors = {
    url: {
      isError: false,
      text: "",
    },
    email: {
      isError: false,
      text: "",
    },
    finance_email: {
      isError: false,
      text: "",
    },
    in_dev_url: {
      isError: false,
      text: "",
    },
    no_web_url: {
      isError: false,
      text: "",
    },
    npwp: {
      isError: false,
      text: "",
    },
    npwp_name: {
      isError: false,
      text: "",
    },
    headquarters_address: {
      isError: false,
      text: "",
    },
    outlet_address: {
      isError: this.passportStore?.outlet.outletAddress ? false : undefined,
      text: undefined,
    },
    outlet_city: {
      isError: this.selectedPostalComboBoxOptions ? false : undefined,
      text: undefined,
    },
    physical_business_upload: {
      isError: this.hasPhysicalBusiness ? false : undefined,
      text: undefined,
    },
  };

  /**
   * FIXME: Change back to `1` as the value of aktaFileUploaderCount and skFileUploaderCount later
   * @author sendy.suryana
   * @date 07 December 2020
   * @time 13:37 GMT+7
   */
  @observable
  aktaFileUploaderCount = 1;

  @observable
  skFileUploaderCount = 1;

  @observable
  withDevPartner = false;

  aktaFiles: IOutletFiles[] = new Array(5).fill(null).map((data, index) => {
    return {
      id: undefined,
      name: `midtrans.akta${index + 1}`,
      url: undefined,
      media_type: undefined,
      metadata: {
        tag: "",
      },
      _destroy: true,
    };
  });

  menkehFiles: IOutletFiles[] = new Array(5).fill(null).map((data, index) => {
    return {
      id: undefined,
      name: `midtrans.menkeh${index + 1}`,
      url: undefined,
      media_type: undefined,
      metadata: {
        tag: "",
      },
      _destroy: true,
    };
  });

  postalComboBoxOptions = POSTAL_CODE.map(
    ({ city_short, postal_code, province }) => ({
      id: `${postal_code} - ${city_short} - ${province}`,
      key: `${postal_code} - ${city_short} - ${province}`,
    })
  );

  @observable
  form: Partial<BusinessInfoForm> = {
    partner_id: "",
    entity_type: "",
    business_category: "",
    email: "",
    finance_email: "",
    npwp_url: "",
    npwp_name: "",
    npwp: "",
    headquarters_address: "",
    shipping_area: "Indonesia",
    url: "",
    development_url: "",
    social_media_url: "",
    marketplace_url: "",
    files: [...this.aktaFiles, ...this.menkehFiles],
    merchant_name: "",
    official_name: "",
    outlet_address: "",
    outlet_city: "",
    outlet_postal_code: "",
    outlet_province: "",
    aspi: { mcc: "" },
    business_scale: null,
    business_photo: {
      url: undefined,
      temp: undefined,
    },
  };

  @observable
  isOssEnabled = fromEnv("REACT_APP_OSS_ENABLED") === "true";

  @computed
  get showTooltip(): boolean {
    return this.websiteOption !== "" && this.websiteOption !== "no";
  }

  @computed
  get isWithDevPartnerValid(): boolean {
    if (this.withDevPartner) {
      return !!this.form.partner_id;
    }
    return true;
  }

  @computed
  get shouldDisplayDevPartner(): boolean {
    if (this.withDevPartner && this.form.partner_id) {
      return !!DEV_PARTNERS.find((v) => v.value === this.form.partner_id);
    }
    return true;
  }

  @computed
  get isShowUrlInput(): boolean {
    return this.websiteOption === "yes" || this.websiteOption === "in_dev";
  }

  @computed
  get isShowAdditionalInfo(): boolean {
    return this.websiteOption === "no" || this.websiteOption === "in_dev";
  }

  @computed get isShowWebGuideText(): boolean {
    return this.websiteOption === "in_dev" || this.websiteOption === "yes";
  }

  @computed
  get isUrlFormValid(): boolean {
    if (this.websiteOption === "yes") {
      return !!this.form.url && !this.inputErrors.url.isError;
    }
    if (this.websiteOption === "in_dev") {
      return (
        !!this.form.url &&
        !!(
          this.form.development_url ||
          this.form.social_media_url ||
          this.form.marketplace_url
        ) &&
        !this.inputErrors.url.isError &&
        !this.inputErrors.in_dev_url.isError
      );
    }
    if (this.websiteOption === "no") {
      if (this.linksOption === "marketplace_url") {
        return (
          !!this.form.marketplace_url && !this.inputErrors.no_web_url.isError
        );
      }
      return (
        !!this.form.social_media_url && !this.inputErrors.no_web_url.isError
      );
    }
    return false;
  }

  @computed
  get isPhysicalBusinessValid(): boolean {
    if (!this.hasPhysicalBusiness) return true;
    return (
      !!this.form.business_photo?.url &&
      !this.inputErrors.physical_business_upload.isError
    );
  }

  @computed
  get isBasicFormValid(): boolean {
    return !!(
      this.form.business_category &&
      this.isUrlFormValid &&
      this.form.email &&
      this.form.finance_email &&
      this.isWithDevPartnerValid &&
      this.form.outlet_address &&
      this.selectedPostalComboBoxOptions &&
      this.form.outlet_postal_code &&
      this.form.outlet_city &&
      this.form.outlet_province &&
      this.form.business_scale &&
      this.isPhysicalBusinessValid
    );
  }

  @computed
  get isIndividualFormValid(): boolean {
    return (
      this.isBasicFormValid &&
      !this.inputErrors.email.isError &&
      !this.inputErrors.finance_email.isError
    );
  }

  @computed
  get hasUploadedCorporateFiles(): boolean {
    const aktaDoc = this.form.files?.find((file) => {
      if (file?.name?.startsWith("midtrans.akta") && file?.url) {
        return file;
      }
    });

    const menkehDoc = this.form.files?.find((file) => {
      if (file?.name?.startsWith("midtrans.menkeh") && file?.url) {
        return file;
      }
    });

    return !!(aktaDoc && menkehDoc);
  }

  @computed
  get isCorporateFormValid(): boolean {
    return (
      this.isBasicFormValid &&
      !!(
        this.form.entity_type &&
        this.form.npwp_url &&
        this.form.npwp_name &&
        this.form.npwp &&
        this.form.headquarters_address &&
        this.hasUploadedCorporateFiles
      ) &&
      !this.inputErrors.email.isError &&
      !this.inputErrors.finance_email.isError &&
      !this.inputErrors.npwp.isError
    );
  }

  @computed
  get isCardPaymentFormValid(): boolean {
    return !!this.form.shipping_area;
  }

  @computed
  get parsedPostalComboBoxOptions(): IPassportParsedPostalComboBox {
    const [
      postalCode,
      city,
      province,
    ] = this.selectedPostalComboBoxOptions
      ?.split("-")
      ?.map((option) => option.trim()) || ["", "", ""];

    return {
      city,
      province,
      postal_code: postalCode,
    };
  }

  isValid(isCorporate: boolean, isCardPayment: boolean): boolean {
    if (isCorporate) {
      return (
        this.isCorporateFormValid &&
        (isCardPayment ? this.isCardPaymentFormValid : true)
      );
    } else {
      return (
        this.isIndividualFormValid &&
        (isCardPayment ? this.isCardPaymentFormValid : true)
      );
    }
  }

  remapFile = (file: IOutletFiles): IOutletFiles => {
    if (!file.url) {
      return {
        ...file,
        _destroy: true,
      };
    }
    return file;
  };

  toActivationJSON = (): Partial<IActivationJSON> => {
    return {
      business_scale: this.form.business_scale!!,
      business_photo_url: this.form.business_photo?.url,
    };
  };

  toJSON = (): Partial<IMerchantJSON> => {
    let clonedForm = Object.assign({}, this.form);
    delete clonedForm.entity_type;
    delete clonedForm.business_category;
    delete clonedForm.business_photo;
    delete clonedForm.business_scale;

    clonedForm = cleanObject({
      ...clonedForm,
      files: clonedForm.files?.map(this.remapFile),
      business_categories: this.form.business_category
        ? [this.form.business_category]
        : null,
      partner_id: Number(this.form.partner_id) || null,
      official_name: `${this.form.entity_type} ${this.form.merchant_name}`.trim(),
      notification_settings: {
        email_support: this.form.email,
        email_finance: this.form.finance_email,
      },
    });

    clonedForm.development_url = this.appendPrefixUrl(
      this.form.development_url!
    );
    clonedForm.social_media_url = this.appendPrefixUrl(
      this.form.social_media_url!
    );
    clonedForm.development_url = this.appendPrefixUrl(
      this.form.development_url!
    );
    clonedForm.url = this.appendPrefixUrl(this.form.url!);
    clonedForm.marketplace_url = this.appendPrefixUrl(
      this.form.marketplace_url!
    );
    clonedForm.url = this.websiteOption === "no" ? "" : clonedForm.url!;
    if (!this.withDevPartner) {
      clonedForm.partner_id = null;
    }
    return clonedForm as Partial<IMerchantJSON>;
  };

  @action
  setValue = (name: keyof BusinessInfoForm, value: any) => {
    this.form[name] = value;
  };

  @action
  validatePhysicalBusinessPhoto = () => {
    this.inputErrors.physical_business_upload.isError = !this.form
      .business_photo?.url;
  };

  @action
  setPhysicalBusinessFile = (file: File) => {
    this.form.business_photo = {
      url: this.passportStore?.fetchUrl,
      temp: file,
    };
    this.validatePhysicalBusinessPhoto();
  };

  @action
  deletePhysicalBusinessFile = () => {
    this.form.business_photo = {
      url: undefined,
      temp: undefined,
    };
  };

  @action
  getAvailableFileName = (purpose: string): string => {
    return this.form.files?.find(
      (file) => file.name?.includes(purpose) && !file.url
    )?.name!;
  };

  appendPrefixUrl = (url: string): string | null => {
    if (url === "") return null;

    const isStartWithPrefixUrl =
      url.startsWith("http://") || url.startsWith("https://");
    let appendedUrl = url;

    if (!isStartWithPrefixUrl) {
      appendedUrl = `https://${url}`;
    }

    return appendedUrl;
  };

  @action
  setShipping = (shipping: string) => {
    this.shipping = shipping;
    if (shipping === "indonesia") {
      this.setValue("shipping_area", "Indonesia");
    } else {
      this.setValue("shipping_area", "");
    }
  };

  @action
  resetWebFilling = () => {
    this.form = {
      ...this.form,
      development_url: "",
      marketplace_url: "",
      social_media_url: "",
    };
    if (this.websiteOption === "no") {
      this.form.url = "";
    }
  };

  @action
  resetWebOption = () => {
    this.linksOption = "";
  };

  @action
  validateInput = (
    key:
      | "url"
      | "in_dev_url"
      | "no_web_url"
      | "npwp"
      | "npwp_name"
      | "headquarters_address"
      | "email"
      | "finance_email"
      | "outlet_address",
    field: keyof Partial<BusinessInfoForm>
  ) => {
    const validateEmail = /^([a-zA-Z0-9+_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/;
    const validateUrl = /^((https?):\/\/)?([\w\d]+((\.{1}|-{1,})[\w\d]+)*(\.\w{2,})|(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))(:\d{1,5})?((\/|\?).*)?$/;

    const validateNpwp = /^\S\d{14,15}$/;
    if (this.form[field] === "") {
      this.inputErrors[key].isError = true;
      if (key === "email") {
        this.inputErrors[key].text =
          "business_info.errors.business_email.required";
      } else if (key === "finance_email") {
        this.inputErrors[key].text =
          "business_info.errors.finance_email.required";
      } else if (
        key === "url" ||
        key === "in_dev_url" ||
        key === "no_web_url"
      ) {
        this.inputErrors[key].text = "business_info.errors.link.required";
      } else if (key === "npwp") {
        this.inputErrors[key].text =
          "business_info.errors.npwp.number.required";
      } else {
        this.inputErrors[key].text = "generic.input.error.required";
      }

      return;
    }
    if (key === "email" && !validateEmail.test(this.form.email as any)) {
      this.inputErrors.email.isError = true;
      this.inputErrors.email.text = "business_info.input.error.email";

      return;
    }
    if (
      key === "finance_email" &&
      !validateEmail.test(this.form.finance_email as any)
    ) {
      this.inputErrors.finance_email.isError = true;
      this.inputErrors.finance_email.text = "business_info.input.error.email";

      return;
    }
    if (key === "npwp" && !validateNpwp.test(this.form.npwp as any)) {
      this.inputErrors.npwp.isError = true;
      this.inputErrors.npwp.text = "owner_info.input.error.npwp";

      return;
    }
    if (
      (key === "url" || key === "in_dev_url" || key === "no_web_url") &&
      !validateUrl.test(this.form[field] as any)
    ) {
      this.inputErrors[key].isError = true;
      this.inputErrors[key].text = "business_info.input.error.url";

      return;
    }

    this.inputErrors[key].isError = false;
    this.inputErrors[key].text = "";
  };

  @action
  resetMarketplaceSocialMedia = () => {
    this.setValue("social_media_url", "");
    this.setValue("marketplace_url", "");
    this.setValue("development_url", "");
  };

  setFile = (fileName: string, file: File, url: string) => {
    return {
      media_type: file.type,
      name: fileName,
      url: url,
      metadata: {
        tag: file.name,
      },
    };
  };

  previewUrl = (fileName: string): string[] | undefined => {
    if (this.form?.files?.length! > 0) {
      return this.form.files
        ?.filter((file) => file.name?.includes(fileName) && file.url)
        .map((file) => file.url!);
    }
  };

  getFilesByName = (fileName: string): IOutletFiles[] | undefined => {
    if (this.form?.files?.length! > 0) {
      return this.form?.files?.filter(
        (file) => file.name?.includes(fileName) && file.url
      );
    }
  };

  @action
  deleteFile = (fileName: string) => {
    const index = this.form?.files?.findIndex((file) =>
      file.name?.includes(fileName)
    );
    if (index! > -1) {
      this.form.files![index!]!.url = null;
      if (fileName.includes("akta") && this.aktaFileUploaderCount > 1) {
        this.aktaFileUploaderCount -= 1;
      }
      if (fileName.includes("menkeh") && this.skFileUploaderCount > 1) {
        this.skFileUploaderCount -= 1;
      }
    }
  };

  @action
  applySelectedComboBox(value: string) {
    this.selectedPostalComboBoxOptions = value;
    this.form.outlet_city = this.parsedPostalComboBoxOptions?.city || "";
    this.form.outlet_postal_code =
      this.parsedPostalComboBoxOptions?.postal_code || "";
    this.form.outlet_province =
      this.parsedPostalComboBoxOptions?.province || "";
  }

  parseEntityType = (officialName?: string): string => {
    const index = ENTITY_TYPES.findIndex((value) =>
      officialName?.includes(value)
    );
    if (index > -1) {
      return ENTITY_TYPES[index];
    }
    return "";
  };

  mergeMerchantFiles = (
    file: IOutletFiles,
    merchantFiles: IOutletFiles[]
  ): IOutletFiles => {
    const merchantFile = merchantFiles.find(
      (files) => files.name === file.name
    );
    if (merchantFile) {
      return merchantFile;
    }
    return file;
  };

  @action
  fillForm = (merchant: Merchant, authUser: User, activation: Activation) => {
    const { form } = this;

    this.form = {
      partner_id: merchant.json?.partner_id?.toString() || form.partner_id,
      entity_type:
        this.parseEntityType(merchant.json?.official_name!) || form.entity_type,
      business_category:
        merchant.json?.business_categories?.[0] || form.business_category,
      email: merchant.email || authUser.email || form.email,
      finance_email:
        merchant.json.notification_settings?.email_finance ||
        form.finance_email,
      npwp_url:
        merchant.json?.npwp_url! ||
        merchant.files.find(
          ({ name }) => name === `npwp_${merchant.businessType}`
        )?.url ||
        form.npwp_url,
      npwp_name: merchant.json?.npwp_name || form.npwp_name,
      npwp: merchant.json?.npwp || form.npwp,
      headquarters_address:
        merchant.json?.headquarters_address || form.headquarters_address,
      shipping_area: merchant.json?.shipping_area || form.shipping_area,
      url: merchant.json?.url || form.url,
      development_url: merchant.json?.development_url || form.development_url,
      social_media_url:
        merchant.json?.social_media_url || form.social_media_url,
      marketplace_url: merchant.json?.marketplace_url || form.marketplace_url,
      files: [
        ...merchant.files.filter((file) =>
          ["midtrans.akta", "midtrans.menkeh"].every(
            (name) => !file.name?.includes(name)
          )
        ),
        ...this.aktaFiles.map((file) =>
          this.mergeMerchantFiles(file, merchant.files)
        ),
        ...this.menkehFiles.map((file) =>
          this.mergeMerchantFiles(file, merchant.files)
        ),
      ],
      merchant_name: merchant.name || form.merchant_name,
      official_name: merchant.json?.official_name || form.official_name,
      notes: merchant.json?.notes || form.notes,
      outlet_address: merchant.outletAddress,
      aspi: form.aspi,
      business_photo: activation.businessPhotoUrl
        ? { url: activation.businessPhotoUrl }
        : form.business_photo,
      business_scale: activation.businessScale || form.business_scale,
    };

    if (!!this.form.business_photo?.url) {
      this.hasPhysicalBusiness = true;
    }

    this.withDevPartner = !!this.form.partner_id;
    if (
      merchant.outletPostalcode &&
      merchant.outletCity &&
      merchant.outletProvince
    ) {
      this.applySelectedComboBox(
        `${merchant.outletPostalcode} - ${merchant.outletCity} - ${merchant.outletProvince}`
      );
    } else {
      this.applySelectedComboBox("");
    }

    if (merchant.json?.shipping_area) {
      this.shipping =
        merchant.json.shipping_area === "Indonesia" ? "indonesia" : "other";
    }
    if (merchant.json?.url) {
      if (
        !(
          merchant.json?.development_url ||
          merchant.json?.social_media_url ||
          merchant.json?.marketplace_url
        )
      ) {
        this.websiteOption = "yes";
      } else {
        this.websiteOption = "in_dev";
      }
    } else {
      if (merchant.json?.social_media_url || merchant.json?.marketplace_url) {
        this.websiteOption = "no";
      }
    }

    if (merchant.json?.development_url) {
      this.linksOption = "development_url";
    }
    if (merchant.json?.social_media_url) {
      this.linksOption = "social_media_url";
    }
    if (merchant.json?.marketplace_url) {
      this.linksOption = "marketplace_url";
    }

    if (this.form?.files?.length! > 0) {
      this.aktaFileUploaderCount =
        this.form.files?.filter(
          ({ name, url }) => name?.includes("midtrans.akta") && url
        )?.length! || 1;
      this.skFileUploaderCount =
        this.form.files?.filter(
          ({ name, url }) => name?.includes("midtrans.menkeh") && url
        )?.length! || 1;
    }
  };

  @action
  @asyncRequest("uploadFilePhysicalBusiness")
  async uploadFilePhysicalBusiness(userId: number, file: File) {
    if (this.isOssEnabled) {
      await this.passportStore?.uploadFileOss(file, "business");
    } else {
      await this.passportStore?.uploadFile(userId, file);
    }
  }

  @action
  @asyncRequest("uploadFileBusinessInfoNpwp")
  async uploadFileBusinessInfoNpwp(userId: number, file: File) {
    if (this.isOssEnabled) {
      return this.passportStore?.uploadFileOss(file, "npwp");
    } else {
      return this.passportStore?.uploadFile(userId, file);
    }
  }

  @action
  @asyncRequest("uploadFileBusinessInfo")
  async uploadFileBusinessInfo(userId: number, file: File, purpose: string) {
    if (this.isOssEnabled) {
      const documentType = purpose.split(".")[1];
      return this.passportStore?.uploadFileOss(file, documentType);
    } else {
      return this.passportStore?.uploadFile(userId, file);
    }
  }

  validateRequiredFields()  {
    const requiredFields = [
      "email",
      "finance_email",
      "url",
      "in_dev_url",
      "no_web_url",
      "npwp",
      "npwp_name",
      "outlet_address",
      "headquarters_address"
    ];

    requiredFields.forEach((key) => {
      this.validateInput(key as any, key as keyof BusinessInfoForm);
    });
  }
}

export const BusinessInfoContext = createContext(new BusinessInfoState());

export const useBusinessInfoState = () => useContext(BusinessInfoContext);
